import {useEffect, useRef, useState} from "react";
import {Button} from "react-bootstrap";
import {createPortal} from "react-dom";
import {FaAngleDoubleLeft} from "react-icons/fa";
import {DrawerSelectProps} from "../types/props";

const DrawerSelect = ({selectOptions, onSelect}: DrawerSelectProps) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({top: 0, left: 0});

  const btnRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [selected, setSelected] = useState(Object.keys(selectOptions)[0]);
  const drawerWidth = 140;

  const updateCoords = (rect: DOMRect) => {
    const anchorTop = rect.top + window.scrollY;
    const anchorLeft = rect.left + window.scrollX;
    const viewportLeft = window.scrollX;
    const viewportRight = viewportLeft + window.innerWidth;
    const buttonWidth = rect.width || 40;
    const edgePadding = 8;

    const leftCandidate = anchorLeft - drawerWidth; // default: slide to the left
    const rightCandidate = anchorLeft + buttonWidth; // fallback: slide to the right

    let left = leftCandidate;

    // If left side would overflow viewport, try right side. If both fail, clamp.
    if (leftCandidate < viewportLeft + edgePadding) {
      if (rightCandidate + drawerWidth <= viewportRight - edgePadding) {
        left = rightCandidate;
      } else {
        left = Math.max(viewportLeft + edgePadding, viewportRight - drawerWidth - edgePadding);
      }
    }

    setCoords({top: anchorTop, left});
  };

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current &&
        btnRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        !btnRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Update drawer position on window resize/scroll
  useEffect(() => {
    const handleScroll = () => {
      if (open && btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect();
        updateCoords(rect);
      }
    };
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [open]);

  const toggle = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      updateCoords(rect);
      setOpen(!open);
    }
  };

  const handleSelect = (key: string) => {
    return () => {
      setSelected(key);
      setOpen(false);
      onSelect(key);
    };
  }

  return (
    <>
      <div id="drawer-select" className="d-flex flex-column align-items-center" ref={btnRef}>
        <Button
          variant="outline-secondary"
          size="sm"
          className="d-flex flex-column align-items-center justify-content-center border-0 pb-3 pt-1 h-100 p-2 border-tr-0 w-100"
          onClick={toggle}
        >
          {/* Icon at top */}
          <span><FaAngleDoubleLeft/></span>

          {/* Vertical text */}
          <span className="mt-1 text-center vertical-text">
          {selectOptions[selected]}
        </span>
        </Button>
      </div>

      {/* Drawer rendered in portal */}
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="bg-white border rounded shadow-sm d-flex flex-column p-2 position-absolute"
            style={{
              top: coords.top,
              left: coords.left,
              width: drawerWidth,
              zIndex: 1050,
            }}
          >
            {Object.entries(selectOptions).map(([key, name]) => (
              <Button
                key={key}
                size="sm"
                variant="light"
                className="text-start mb-1"
                onClick={handleSelect(key)}>
                {name}
              </Button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}

export default DrawerSelect;
